import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Table from '../table';
import AwpDropdown from '@awp/components/Common/DropDown/AwpDropdown';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint } from '../../helpers/UIHelper';
import { debounce } from '../../helpers/Utils';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import { sortByNumericThenAlpha } from '../../helpers/sorter';
import { REST_API, STATE_STATUS, ACCESS_REQUEST_STATUS } from '@awp/constants';
import ButtonActionMenu from '../../tables/buttonActionMenu';
import { Tooltip } from 'react-tooltip';
import ConfirmModal from '../confirmModal';
import Authorize from '@awp/components/Authorize';
import { Permissions } from '@awp/constants';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { selectAccessRequestStatuses } from '@awp/stores/dropdownsSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchSystemNotifications } from '@awp/stores/systemNotificationsSlice';
import 'react-tooltip/dist/react-tooltip.css';
import { AwpContainer } from '../Common/AwpContainer';
import { Crud } from '@awp/shared/Crud';

const AccessRequestTable = () => {
  const location = useLocation();
  const locationState = location?.state;
  const [isMedium, setIsMedium] = useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
  const [isLarge, setIsLarge] = useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(locationState?.status);
  const [errors, setErrors] = useState(null);
  const statusesDropdown = useSelector(selectAccessRequestStatuses);
  const dispatch = useDispatch();

  //confirmModal
  const [cfmModal, setCfmModal] = useState({ show: false, stateMessage: '', state: '' });
  const [cfmConfig, setCfmConfig] = useState({
    title: '',
    message: '',
    cfmBtnText: '',
    cancelBtnText: '',
    cfmAction: null,
  });

  const crud = new Crud('Access Requests');

  const toggleCfmModal = () => {
    setCfmModal({ show: !cfmModal.show, stateMessage: '', state: '' });
  };

  useEffect(() => {
    const handleResize = debounce(() => {
      setIsMedium(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
      setIsLarge(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE));
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const updateRequestStatus = (data, id, accessRequestStatus) => {
    const newData = [...data];
    const index = newData.findIndex((x) => x.Id === id);
    if (index < 0) return;

    newData[index] = { ...newData[index], RequestStatus: accessRequestStatus, NotSelectable: true };
    setData(newData);
  };

  const updateAccessRequest = (data, id, url, message, accessRequestStatus) => {
    setCfmModal({ show: true, stateMessage: `${message} ${id}...`, state: STATE_STATUS.LOADING });

    url = url.replace(/\{id\}/, id);

    PutJson(url)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Access request has been updated');
          updateRequestStatus(data, id, accessRequestStatus);
          dispatch(fetchSystemNotifications());
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setCfmModal({ show: false, stateMessage: '', state: '' });
      });
  };

  const onApproveBtnClick = (data, id) => {
    setCfmConfig({
      title: 'Confirm Approval',
      message: 'Once approved, the requester will be given a general user role and will have access to the system.',
      cfmBtnText: 'Yes, Confirm Approval',
      cancelBtnText: 'Cancel',
      cfmAction: () =>
        updateAccessRequest(data, id, REST_API.ACCESS_REQUEST_APPROVE, 'Approving', ACCESS_REQUEST_STATUS.GRANTED),
    });
    setCfmModal({ show: true, stateMessage: '', state: '' });
  };

  const onDenyBtnClick = (data, id) => {
    setCfmConfig({
      title: 'Confirm Denial',
      message:
        'Once denied, the requester will no longer have access to the system and will need to resubmit a request.',
      cfmBtnText: 'Yes, Deny Request',
      cancelBtnText: 'Cancel',
      cfmAction: () =>
        updateAccessRequest(data, id, REST_API.ACCESS_REQUEST_DENY, 'Denying', ACCESS_REQUEST_STATUS.DENIED),
    });
    setCfmModal({ show: true, stateMessage: '', state: '' });
  };

  /* istanbul ignore next */
  const columns = useMemo(
    () => [
      {
        Header: 'Request Date',
        accessor: 'RequestDate',
        style: { width: '160rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'First Name',
        accessor: 'FirstName',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Last Name',
        accessor: 'Surname',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'User ID',
        accessor: 'BusinessIdentifier',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
        Cell: (row) => {
          const data = row.row.original;
          const id = `row-${data.Id}`;
          return (
            <>
              <span data-tooltip-content data-tooltip-id={id}>
                {row.value}
              </span>
              <Tooltip
                id={id}
                place="bottom"
                type="dark"
                closeOnResize={true}
                disableStyleInjection="core"
                render={() => (
                  <>
                    <b>{data.DisplayName}</b>
                    <br />
                    {data.Organization ? (
                      <>
                        <span>{data.Organization}</span>
                        <br />
                      </>
                    ) : (
                      <></>
                    )}
                    {data.EmailAddress}
                  </>
                )}
              />
            </>
          );
        },
      },
      {
        Header: 'User Type',
        accessor: 'UserType',
        style: { width: '120rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Message',
        accessor: 'SubmissionComment',
        show: isMedium,
        style: { width: '350rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        style: { width: '120rem' },
        Cell: (p) => {
          return p.row.original.RequestStatus === ACCESS_REQUEST_STATUS.PENDING ? (
            <Authorize
              requires={[Permissions.AdminDefault]}
              unauthorizedError={<ButtonActionMenu text={p.row.original.RequestStatus}></ButtonActionMenu>}
            >
              <ButtonActionMenu
                button1={{ text: 'Approve', onClick: () => onApproveBtnClick(p.data, p.value) }}
                button2={{ text: 'Deny', onClick: () => onDenyBtnClick(p.data, p.value) }}
              ></ButtonActionMenu>
            </Authorize>
          ) : (
            <ButtonActionMenu text={p.row.original.RequestStatus}></ButtonActionMenu>
          );
        },
      },
    ],
    [isLarge, isMedium],
  );

  const selectableRows = {
    enabled: false, //no batch actions are implemented yet
    options: statusesDropdown.options,
    handleChange: (statusValue, accessRequests) => {
      const requestData = {
        StatusId: statusValue,
        Ids: accessRequests.map((x) => x.original.Id),
      };
    },
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    let url = REST_API.ACCESS_REQUEST;

    url += '?status=' + (status?.value || '');

    GetJson(url)
      .then((response) => response.json())
      .then((data) => {
        setData(data);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <AwpContainer>
      <div className="d-flex">{crud.getHeader()}</div>
      <div className="container mb-3">
        <div className="row">
          <div className="col-sm-3">
            <AwpDropdown
              isMulti={false}
              selectedValues={status}
              onChange={setStatus}
              data={statusesDropdown}
              title="Status"
            />
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        data={data}
        fetchData={fetchData}
        loading={loading}
        search={''}
        filters={[status]}
        selectableRows={selectableRows}
        dataTriggers={[]}
        defaultSortBy={'RequestDate'}
        defaultDesc={true}
      />
      <ConfirmModal
        isOpen={cfmModal.show}
        toggleModal={toggleCfmModal}
        cfmConfig={cfmConfig}
        actionState={cfmModal.state}
        actionStateMessage={cfmModal.stateMessage}
      ></ConfirmModal>
      <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
    </AwpContainer>
  );
};

export default AccessRequestTable;
